import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { persist } from 'zustand/middleware'
import type { Socket } from 'socket.io-client'

export type Theme = 'dark' | 'light' | 'system'
export type AIMode = 'ask' | 'edit' | 'agent'
export type AIModel = 'auto' | 'gemini-2.5-flash' | 'gemini-2.5-pro' | 'gpt-4o' | 'gpt-4o-mini' | 'claude-4-sonnet' | 'claude-4-opus' | 'deepseek-chat' | 'deepseek-v3' | 'llama-4' | 'grok-3' | 'openai/gpt-4o' | 'anthropic/claude-3.5-sonnet-20240620' | 'nvidia/nemotron-3-ultra-550b-a55b:free' | 'nvidia/nemotron-3-nano-omni-30b-a3b-reasoning:free' | 'nvidia/nemotron-3.5-content-safety:free' | 'poolside/laguna-m.1:free' | 'poolside/laguna-xs.2:free' | 'openrouter/owl-alpha' | 'mistral-medium-3.5' | 'openai/gpt-oss-120b:free' | 'openai/gpt-oss-20b:free' | 'meta-llama/llama-3.2-3b-instruct:free'
export type PanelLayout = 'default' | 'zen' | 'wide'

export interface FileTab {
  id: string
  path: string
  name: string
  language: string
  isDirty: boolean
  content: string
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: number
  codeBlocks?: Array<{ lang: string; code: string }>
  agentRun?: { goal: string; phases: any[]; filesModified: string[] }
}

export interface AppState {
  // UI
  theme: Theme
  layout: PanelLayout
  sidebarWidth: number
  aiPanelWidth: number
  isSidebarOpen: boolean
  isAIPanelOpen: boolean

  // Editor
  openTabs: FileTab[]
  activeTabId: string | null

  // AI
  aiMode: AIMode
  aiModel: AIModel
  chatMessages: ChatMessage[]
  isAIThinking: boolean
  contextPills: string[]

  // Collab
  isCollabActive: boolean
  roomId: string | null

  // Voice
  isVoiceActive: boolean

  // Snapshots (for undo AI changes)
  fileSnapshots: Record<string, string>

  // Agent-applied file diffs (for editor green/red highlights)
  agentFilePatches: Record<string, { oldContent: string; newContent: string }>

  // Auto-save
  autoSave: boolean

  // Actions
  setTheme: (theme: Theme) => void
  setAIMode: (mode: AIMode) => void
  setAIModel: (model: AIModel) => void
  openTab: (file: Omit<FileTab, 'isDirty'>) => void
  closeTab: (id: string) => void
  setActiveTab: (id: string) => void
  markDirty: (id: string, content: string) => void
  addMessage: (msg: Omit<ChatMessage, 'id' | 'timestamp'>) => void
  setAIThinking: (thinking: boolean) => void
  addContextPill: (label: string) => void
  removeContextPill: (label: string) => void
  toggleSidebar: () => void
  toggleAIPanel: () => void
  toggleVoice: () => void
  toggleAutoSave: () => void
  snapshotFile: (path: string, content?: string) => void
  undoFileChange: (path: string) => void
  getSnapshot: (path: string) => string | undefined
  setAgentFilePatch: (path: string, oldContent: string, newContent: string) => void
  clearAgentFilePatch: (path: string) => void
  saveTab: (id: string) => Promise<void>
  markClean: (id: string) => void
}

export const useAppStore = create<AppState>()(
  persist(
    immer((set) => ({
      // Defaults
      theme: 'dark',
      layout: 'default',
      sidebarWidth: 220,
      aiPanelWidth: 320,
      isSidebarOpen: true,
      isAIPanelOpen: true,

      openTabs: [],
      activeTabId: null,

      aiMode: 'ask',
      aiModel: 'auto',
      chatMessages: [],
      isAIThinking: false,
      contextPills: [],

      isCollabActive: false,
      roomId: null,

      isVoiceActive: false,

      fileSnapshots: {},

      agentFilePatches: {},

      autoSave: false,

      // Actions
      setTheme: (theme) => set((s) => { s.theme = theme }),

      setAIMode: (mode) => set((s) => { s.aiMode = mode }),

      setAIModel: (model) => set((s) => { s.aiModel = model }),

      openTab: (file) =>
        set((s) => {
          const exists = s.openTabs.find((t) => t.id === file.id)
          if (!exists) {
            s.openTabs.push({ ...file, isDirty: false })
          }
          s.activeTabId = file.id
        }),

      closeTab: (id) =>
        set((s) => {
          s.openTabs = s.openTabs.filter((t) => t.id !== id)
          if (s.activeTabId === id) {
            s.activeTabId = s.openTabs.at(-1)?.id ?? null
          }
        }),

      setActiveTab: (id) => set((s) => { s.activeTabId = id }),

      markDirty: (id, content) => {
        let tabPath: string | null = null
        set((s) => {
          const tab = s.openTabs.find((t) => t.id === id)
          if (tab) {
            tab.isDirty = true
            tab.content = content
            tabPath = tab.path
          }
        })
        // Broadcast to collab room if not a remote incoming change
        if (tabPath && !(window as any).__pulseRemoteIncoming) {
          const roomId = localStorage.getItem('pulse-collab-room')
          if (roomId) {
            const sock = (window as any).__pulseCollabSocket as Socket | null
            if (sock?.connected) {
              sock.emit('file:change', { roomId, path: (tabPath as string).replace(/^\//, ''), content })
            }
          }
        }
      },

      addMessage: (msg) =>
        set((s) => {
          s.chatMessages.push({
            ...msg,
            id: crypto.randomUUID(),
            timestamp: Date.now(),
          })
        }),

      setAIThinking: (thinking) => set((s) => { s.isAIThinking = thinking }),

      addContextPill: (label) =>
        set((s) => {
          if (!s.contextPills.includes(label)) s.contextPills.push(label)
        }),

      removeContextPill: (label) =>
        set((s) => {
          s.contextPills = s.contextPills.filter((p) => p !== label)
        }),

      toggleSidebar: () => set((s) => { s.isSidebarOpen = !s.isSidebarOpen }),

      toggleAIPanel: () => set((s) => { s.isAIPanelOpen = !s.isAIPanelOpen }),

      toggleVoice: () => set((s) => { s.isVoiceActive = !s.isVoiceActive }),

      toggleAutoSave: () => set((s) => { s.autoSave = !s.autoSave }),

      snapshotFile: (path, content) =>
        set((s) => {
          if (content !== undefined) {
            s.fileSnapshots[path] = content
            return
          }
          const tab = s.openTabs.find((t) => t.path === path)
          if (tab) s.fileSnapshots[path] = tab.content
        }),

      setAgentFilePatch: (path, oldContent, newContent) =>
        set((s) => {
          s.agentFilePatches[path] = { oldContent, newContent }
        }),

      clearAgentFilePatch: (path) =>
        set((s) => {
          delete s.agentFilePatches[path]
        }),

      undoFileChange: (path) =>
        set((s) => {
          const snapshot = s.fileSnapshots[path]
          if (snapshot === undefined) return
          const tab = s.openTabs.find((t) => t.path === path)
          if (tab) {
            tab.content = snapshot
            tab.isDirty = true
          }
          delete s.fileSnapshots[path]
        }),

      getSnapshot: (path) => {
        return useAppStore.getState().fileSnapshots[path]
      },

      markClean: (id) =>
        set((s) => {
          const tab = s.openTabs.find((t) => t.id === id)
          if (tab) tab.isDirty = false
        }),

      saveTab: async (id) => {
        const tab = useAppStore.getState().openTabs.find((t) => t.id === id)
        if (!tab) return
        const relPath = tab.path.replace(/^\//, '')
        const base = ((import.meta as any).env?.VITE_API_URL ?? '').replace(/\/$/, '')
        const url = base ? `${base}/api/files/write` : '/api/files/write'
        try {
          const { authFetch } = await import('../lib/api')
          const res = await authFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path: relPath, content: tab.content }),
          })
          if (res.ok) {
            useAppStore.getState().markClean(id)
            window.dispatchEvent(new CustomEvent('pulse:files-changed'))
            window.dispatchEvent(new CustomEvent('pulse:file-saved', { detail: { path: relPath } }))
            // Auto-checkpoint for version history
            const versionUrl = base ? `${base}/api/versions` : '/api/versions'
            authFetch(versionUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ filePath: tab.path, content: tab.content, auto: true }),
            }).catch(() => {})
          }
        } catch {}
      },
    })),
    {
      name: 'pulse-app-state',
      partialize: (s) => ({
        theme: s.theme,
        layout: s.layout,
        sidebarWidth: s.sidebarWidth,
        aiPanelWidth: s.aiPanelWidth,
        aiMode: s.aiMode,
        aiModel: s.aiModel,
        autoSave: s.autoSave,
      }),
    }
  )
)
